import React from 'react';
import { Button } from '@mui/material';
import { Link } from 'react-router-dom';

const styles = {
  container: {
    maxWidth: '1100px',
    marginLeft: 'auto',
    marginRight: 'auto',
    padding: '2rem',
    fontFamily: 'Georgia, serif',
    backgroundColor: '#ffffff',
  },
  header: {
    textAlign: 'center' as const,
    marginBottom: '3rem',
    paddingBottom: '2rem',
    borderBottom: '3px solid #8b7355',
  },
  title: {
    fontSize: '3rem',
    marginBottom: '1rem',
    color: '#2c3e50',
    fontWeight: 'bold' as const,
  },
  tagline: {
    fontSize: '1.3rem',
    color: '#555',
    lineHeight: '1.8',
    maxWidth: '800px',
    marginLeft: 'auto',
    marginRight: 'auto',
    fontStyle: 'italic',
  },
  rolesBadges: {
    display: 'flex',
    justifyContent: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap' as const,
    marginTop: '2rem',
  },
  extraLinks: {
    display: 'flex',
    justifyContent: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap' as const,
    margin: '2rem',
  },
  badge: {
    display: 'inline-block',
    backgroundColor: '#8b7355',
    color: '#fff',
    padding: '0.5rem 1rem',
    borderRadius: '20px',
    fontSize: '0.85rem',
    fontWeight: 'bold' as const,
    fontFamily: 'Arial, sans-serif',
  },
  heroSection: {
    display: 'flex',
    gap: '3rem',
    alignItems: 'flex-start',
    marginBottom: '4rem',
    flexWrap: 'wrap' as const,
  },
  heroImage: {
    flex: '0 1 280px',  // Reduce from 350px
    width: '100%',
    height: 'auto',
    maxWidth: '280px',
    borderRadius: '8px',
    border: '3px solid #8b7355',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    display: 'block',
  },
  heroText: {
    flex: '1 1 300px',
  },
  sectionCard: {
    backgroundColor: '#faf9f7',
    padding: '2.5rem',
    marginBottom: '2rem',
    borderRadius: '8px',
    borderLeft: '5px solid #8b7355',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  sectionTitle: {
    fontSize: '1.8rem',
    color: '#2c3e50',
    marginBottom: '1.5rem',
    marginTop: 0,
    fontWeight: 'bold' as const,
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  timelineMarker: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    backgroundColor: '#8b7355',
    color: '#fff',
    borderRadius: '50%',
    fontSize: '1.5rem',
    flexShrink: 0,
  },
  paragraph: {
    fontSize: '1rem',
    lineHeight: '1.8',
    color: '#333',
    marginBottom: '1.5rem',
  },
  pullQuote: {
    fontSize: '1.2rem',
    fontStyle: 'italic',
    color: '#2c3e50',
    borderLeft: '4px solid #8b7355',
    paddingLeft: '1.5rem',
    marginBottom: '2rem',
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem',
  },
  milestonesSection: {
    marginBottom: '4rem',
  },
  milestonesTitle: {
    fontSize: '2rem',
    fontWeight: 'bold' as const,
    color: '#2c3e50',
    marginBottom: '2rem',
    textAlign: 'center' as const,
  },
  milestoneItem: {
    display: 'flex',
    gap: '2rem',
    marginBottom: '2rem',
    paddingBottom: '2rem',
    borderBottom: '1px solid #e5e7eb',
  },
  milestoneYear: {
    fontWeight: 'bold' as const,
    color: '#4f46e5',
    fontSize: '1.1rem',
    minWidth: '100px',
    fontFamily: 'Arial, sans-serif',
  },
  milestoneContent: {
    flex: 1,
  },
  milestoneTitle: {
    fontSize: '1.1rem',
    fontWeight: 'bold' as const,
    color: '#2c3e50',
    marginBottom: '0.5rem',
  },
  milestoneDescription: {
    fontSize: '0.95rem',
    color: '#555',
    lineHeight: '1.6',
  },
} as const;

export default function About(this: any) {
  const morganFamily = require('../images/morgan-family.jpg');
  const roles = ['Preacher', 'Pastor', 'Teacher', 'Student', 'CEO', 'Mentor', 'Friend'];
  const milestones = [
    { year: '1941', title: 'Called to Preach', description: 'At just 16 years old, left family farm to pursue ministry.' },
    { year: '1945-1947', title: 'Seminary & Early Pastorate', description: 'Earned Master of Theology from Southwestern Seminary while serving as pastor of country churches.' },
    { year: '1947', title: 'Married Elizabeth', description: 'Married on June 20th at Southern Illinois University in a chapel wedding.' },
    { year: '1949-1964', title: 'Regional Ministry', description: 'Served churches across Texas and Alabama for fifteen years, including ministry during the civil rights movement.' },
    { year: '1966-1971', title: 'Cliff Temple Baptist Church', description: 'Pastored Cliff Temple Baptist Church in Dallas while earning his Doctor of Theology.' },
    { year: '1971-1989', title: 'Annuity Board President', description: 'Led the Annuity Board of the Southern Baptist Convention for 19 years, impacting countless religious professionals.' },
  ];

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>About Dr. Morgan</h1>
        <p style={styles.tagline}>
          At 16 years of age, he left the family farm to preach and didn't stop until he taught his last class the Sunday before he passed away.
        </p>
        <div style={styles.rolesBadges}>
          {roles.map((role) => (
            <span key={role} style={styles.badge}>{role}</span>
          ))}
        </div>
      </header>

      <section style={styles.milestonesSection}>
        <h2 style={styles.milestonesTitle}>Life & Ministry Milestones</h2>
        {milestones.map((milestone, idx) => (
          <div key={idx} style={styles.milestoneItem}>
            <div style={styles.milestoneYear}>{milestone.year}</div>
            <div style={styles.milestoneContent}>
              <h3 style={styles.milestoneTitle}>{milestone.title}</h3>
              <p style={styles.milestoneDescription}>{milestone.description}</p>
            </div>
          </div>
        ))}
      </section>

      <section style={styles.heroSection}>
        <img style={styles.heroImage} src={morganFamily} alt="Dr. Morgan with family" />
        <div style={styles.heroText}>
          <p style={styles.paragraph}>
            Dr. Darold H. Morgan was an exemplary husband and father who dedicated his life to ministry, education, and service. His contributions spanned multiple decades and touched countless lives through his work as a pastor, educator, and administrator.
          </p>
          <p style={styles.paragraph}>
            From humble beginnings on a family farm to leading major Baptist organizations, Dr. Morgan's journey demonstrates a steadfast commitment to faith, learning, and compassionate leadership.
          </p>
        </div>
      </section>

      <section style={styles.sectionCard}>
        <h2 style={styles.sectionTitle}>
          <span style={styles.timelineMarker}>📖</span>
          Seminary
        </h2>
        <p style={styles.pullQuote}>
          "When the Hildreths invited Darold Morgan and Elizabeth Johnson for a blind date in a 1938 Chevrolet coupe, it was the beginning of a partnership that would last a lifetime."
        </p>
        <p style={styles.paragraph}>
          When the Hildreths invited Darold Morgan and Elizabeth Johnson for a blind date, it meant the four of them would have to ride in a 1938 Chevrolet coupe to the Chicken Shack in Ft. Worth. It was a scenario of "close communion" and real "togetherness" (big words in Baptist life in the 40's). But this was hardly a date to celebrate doctrinal purity. Rather, it was a matter of keeping Seminary dormitory rules. At that time, a young lady and a young man could not car date alone. They either had to double-date or take along a third party. Since the Hildreth's car was a one-seater, Elizabeth had to sit in Darold's lap. Supposedly, that was more moral than dating alone. Nevertheless, he has since maintained that she made quite an impression.
        </p>
        <p style={styles.paragraph}>
          It was not, however, love at first sight. Darold (age 21) was in his second year at Southwestern Seminary. Elizabeth (age 20) had just graduated from college. Neither was ready to "settle down." So their contacts were casual and few. When the time came for Christmas vacation, he did offer to take her to the train in his 1936 Ford which he had just gotten from his brother. They recklessly ignored the double-date rule, rationalizing that this was a taxi service rather than a real date.
        </p>
        <p style={styles.paragraph}>
          But come spring, things began to warm up. He invited her to the Yankee Club banquet on Valentines Day and sent a red rose corsage. By this time, he had been called to two hal-time country churches. Also, he worked part-time in the Seminary dining hall at the beverage counter, and Elizabeth began to drink lots of coffee, in spite of the fact that it gave her terrible headaches. Both of them decided to go to summer school, and when they parted in August, they agreed to write--she from Illinois and he from Kansas.
        </p>
        <p style={styles.paragraph}>
          One month after returning in the fall, they were engaged to be married, with plans for a June wedding. Within a few months, Darold was accepted in the School of Theology for doctoral study, and Elizabeth applied for a teaching position with the Ft. Worth Public School District. They hired her as a pottery and ceramics specialist for all grades.
        </p>
        <p style={styles.paragraph}>
          After graduation in May when both of them earned their Masters degrees, they were married on June 20th, 1947 in Carbondale, Illinois. Their wedding was the first wedding in a new chapel at Southern Illinois University, where Elizabeth's father taught.
        </p>
        <p style={styles.paragraph}>
          After a honeymoon in the Ozarks, they returned to Texas in the Ford, which left a blue cloud of smoke behind it all the way to Ft. Worth. It used so much oil that when they pulled into a service station, they asked the attendant to fill it with oil and see if they needed any gas. The right front window was broken out, which made for considerable dampness during a rainstorm. Furthermore, Elizabeth began to notice that her hosiery was disintegrating around her ankles after she rode in the car. It had to be leaking battery acid. By the grace of God, the Ford never left them stranded.
        </p>
      </section>

      <section style={styles.sectionCard}>
        <h2 style={styles.sectionTitle}>
          <span style={styles.timelineMarker}>🙏</span>
          Early Ministry
        </h2>
        <p style={styles.paragraph}>
          Going to graduate school, pastoring country churches, and teaching school offered a rather full schedule. But youth is dauntless and refuses to consider how ill prepared both of them were for the life into which they were now thrust. Darold, one of five children, was the son of a railroader. Elizabeth and her older sister grew up in a preacher's home until she entered high school when her father was asked to teach at Southern Illinois University. Although she had a teaching degree, she soon discovered that college classes in education were a woefully inadequate preparation for the classroom.
        </p>
        <p style={styles.paragraph}>
          Within a year, Darold was called as a pastor of the First Baptist Church in Milford, Texas. Elizabeth got a teaching position in nearby Italy, Texas at a junior high school. Darold continued his graduate studies as well at the Seminary.
        </p>
        <p style={styles.paragraph}>
          In June 1949, Darold accepted a position as associate pastor at Highland Baptist Church in Dallas while Elizabeth taught at Obadiah Knight Elementary School in Dallas. For the next fifteen years, Darold and Elizabeth served in churches in Texas and Alabama (Bonham, Sulphur Springs, Sherman, Birmingham, and Dallas). They were in Birmingham at the height of the civil rights movement in the early 60's.
        </p>
        <p style={styles.paragraph}>
          Their family grew with two sons and a daughter who didn't seem to mind being preacher's kids. In fact, they liked it and thought it was a privilege.
        </p>
      </section>

      <section style={styles.sectionCard}>
        <h2 style={styles.sectionTitle}>
          <span style={styles.timelineMarker}>💼</span>
          Annuity Board
        </h2>
        <p style={styles.paragraph}>
          In 1966, the family returned to Texas from Alabama and lived in Oak Cliff in Dallas where Darold pastored Cliff Temple Baptist Church. After five years, he was asked to become president of the Annuity Board of the Southern Baptist Convention where he served for nineteen years.
        </p>
        <p style={styles.paragraph}>
          The Morgans moved to Richardson in 1975 after Darold served as interim pastor at First Baptist Richardson. Darold retired from the Annuity board in 1989, having made a lasting impact on the organization and the lives of countless religious professionals and their families.
        </p>
      </section>
      <section style={styles.extraLinks}>
        <Button
            component={Link}
            to="/written-works"
            variant="contained"
            size="large"
            sx={{ fontWeight: 600, bgcolor: '#8b7355', '&:hover': { bgcolor: '#6d5a43' } }}
            >
            Explore Written Works
        </Button>
        <Button
            component={Link}
            to="/sermon-archives"
            variant="contained"
            size="large"
            sx={{ fontWeight: 600, bgcolor: '#4f46e5', '&:hover': { bgcolor: '#3d37b7' } }}
        >
            Listen to Sermons
        </Button>
      </section>
    </div>
  );
}
